import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  AccountBalance,
  Refresh,
  TrendingUp,
  Info,
  Settings,
  Calculate,
  ArrowBack
} from '@mui/icons-material';
import calculatorApi from '../services/calculatorApi';

// Categories that must never flow into the overhead calculation.
// Taxation (VAT, corp tax, PAYE) sits here alongside Stock, Payroll, Personal
// because tax payments are not a cost of delivering a service — they are a
// consequence of profit and must not inflate service prices.
const DEFAULT_EXCLUDED = ['Stock', 'Payroll', 'Personal', 'Taxation', 'QS Rent', 'QS Utilities', 'QS Insurance', 'QS Other'];

const OverheadsCalculator = () => {
  const navigate = useNavigate();

  const [expenses, setExpenses] = useState([]);
  const [expenseSummary, setExpenseSummary] = useState(null);
  const [config, setConfig] = useState({
    excluded_categories: DEFAULT_EXCLUDED,
    calculation_period: 'last12months',
    total_trading_hours: 40,
    utilization_rate: 85
  });
  const [overheadResults, setOverheadResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
  try {
    setLoading(true);
    const [expensesData, summaryData, configData, businessSettings] = await Promise.all([
      calculatorApi.getExpenses(),
      calculatorApi.getExpenseSummary('last12months'),
      calculatorApi.getExpenseSummaryConfig(),
      calculatorApi.getBusinessSettings()
    ]);

    setExpenses(expensesData.data || []);
    setExpenseSummary(summaryData);

    if (configData) {
      const serverExcluded = configData.excluded_categories || DEFAULT_EXCLUDED;
      const mergedExcluded = Array.from(new Set([...serverExcluded, 'Taxation']));
      setConfig(prev => ({
        ...prev,
        ...configData,
        excluded_categories: mergedExcluded,
        total_trading_hours: businessSettings?.total_working_hours || prev.total_trading_hours,
        utilization_rate: businessSettings?.staff_utilization_rate || prev.utilization_rate
      }));
    }

    // Populate results from persisted business settings so they survive a refresh
    if (businessSettings && parseFloat(businessSettings.overhead_per_minute) > 0) {
      setOverheadResults({
        overheadPerMinute: parseFloat(businessSettings.overhead_per_minute),
        overheadHourly: parseFloat(businessSettings.overhead_per_minute) * 60,
        annualOverhead: parseFloat(businessSettings.annual_overhead || 0),
        period: configData?.calculation_period || 'last12months',
        utilizationRate: businessSettings.staff_utilization_rate
      });
    }
  } catch (err) {
    console.error('Error loading overhead data:', err);
    setError('Failed to load expense data');
  } finally {
    setLoading(false);
  }
};

  const handleRecalculate = async () => {
    try {
      setCalculating(true);
      setError(null);

      const result = await calculatorApi.recalculateOverhead({
        period: config.calculation_period,
        excluded_categories: config.excluded_categories,
        utilization_rate: config.utilization_rate
      });

      setOverheadResults(result);
      setSuccess('Overhead rates recalculated successfully');
      setTimeout(() => setSuccess(null), 3000);

      await loadData();
    } catch (err) {
      console.error('Error recalculating overhead:', err);
      setError('Failed to recalculate overhead rates');
    } finally {
      setCalculating(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      // Enforce Taxation exclusion cannot be removed via the dialog
      const toSave = {
        ...config,
        excluded_categories: Array.from(new Set([...config.excluded_categories, 'Taxation']))
      };
      await calculatorApi.updateExpenseSummaryConfig(toSave);
      setConfig(toSave);
      setSuccess('Configuration saved successfully');
      setConfigDialogOpen(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving config:', err);
      setError('Failed to save configuration');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(value || 0);
  };

  const getCategoryTotal = (category) => {
    return expenses
      .filter(e => e.category === category)
      .reduce((sum, e) => sum + parseFloat(e.total || 0), 0);
  };

  const allCategories = [...new Set(
    expenses
      .map(e => e.category)
      .filter(cat => cat != null && cat !== '')
  )].sort();

  const excludedList = config?.excluded_categories || DEFAULT_EXCLUDED;
  const includedCategories = allCategories.filter(c => !excludedList.includes(c));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={() => navigate('/calculator')} sx={{ color: 'white' }} aria-label="Back to calculator">>
              <ArrowBack />
            </IconButton>
            <Box>
              <Typography variant="h4" sx={{ color: 'white', mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                <AccountBalance sx={{ fontSize: 40 }} />
                Overhead Calculator
              </Typography>
              <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                Calculate per-minute overhead from your actual business expenses
              </Typography>
            </Box>
          </Box>
          <Box>
            <Button
              variant="contained"
              startIcon={<Settings />}
              onClick={() => setConfigDialogOpen(true)}
              sx={{
                mr: 2,
                backgroundColor: 'rgba(255,255,255,0.2)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
              }}
            >
              Configure
            </Button>
            <Button
              variant="contained"
              startIcon={calculating ? <CircularProgress size={20} /> : <Calculate />}
              onClick={handleRecalculate}
              disabled={calculating}
              sx={{
                backgroundColor: 'white',
                color: '#667eea',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' }
              }}
            >
              {calculating ? 'Calculating...' : 'Recalculate Overhead'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 3 }}>{success}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Expense Categories</Typography>
              <Divider sx={{ mb: 2 }} />

              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Excluded from overhead calculation:</strong>
                </Typography>
                <Typography variant="body2" component="ul" sx={{ m: 0, pl: 2 }}>
                  <li><strong>Stock</strong> — product costs are calculated per service separately</li>
                  <li><strong>Payroll</strong> — staff labour costs flow through tier hourly rates</li>
                  <li><strong>Taxation</strong> — VAT, corporation tax, and PAYE are consequences of profit, not service costs</li>
                  <li><strong>Personal</strong> — non-business expenditure</li>
                </Typography>
              </Alert>

              {allCategories.length === 0 ? (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    No expense categories found. Please add expenses to your expenditure records first.
                  </Typography>
                </Alert>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Category</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allCategories.map(category => {
                        const total = getCategoryTotal(category);
                        const isExcluded = excludedList.includes(category);
                        return (
                          <TableRow key={category} sx={isExcluded ? { bgcolor: 'grey.100' } : {}}>
                            <TableCell>{category}</TableCell>
                            <TableCell align="right">{formatCurrency(total)}</TableCell>
                            <TableCell>
                              <Chip
                                label={isExcluded ? 'Excluded' : 'Included'}
                                size="small"
                                color={isExcluded ? 'default' : 'success'}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow>
                        <TableCell><strong>Total Overhead (Included)</strong></TableCell>
                        <TableCell align="right">
                          <strong>
                            {formatCurrency(
                              includedCategories.reduce((sum, cat) => sum + getCategoryTotal(cat), 0)
                            )}
                          </strong>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          <Card elevation={2} sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>How Overhead is Calculated</Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Formula:</strong>
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 2 }}>
                  Annual Overhead ÷ (Weekly Hours × 52 Weeks × Utilization Rate × 60 Minutes)
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Weekly Trading Hours</Typography>
                    <Typography variant="body2">{config.total_trading_hours} hours</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Utilization Rate</Typography>
                    <Typography variant="body2">{config.utilization_rate}%</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Calculation Period</Typography>
                    <Typography variant="body2">
                      {config.calculation_period === 'last12months' ? 'Last 12 Months' :
                       config.calculation_period === 'last6months' ? 'Last 6 Months' :
                       config.calculation_period === 'last3months' ? 'Last 3 Months' : 'Current Month'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Utilization Rate</strong> accounts for time not spent on paid services
                  (e.g., cleaning, admin, breaks). Lower rate = higher per-minute overhead.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          {overheadResults ? (
            <Card elevation={2} sx={{ border: 2, borderColor: 'success.main' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <TrendingUp color="success" />
                  <Typography variant="h6">Overhead Rates</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" color="text.secondary">Per Hour</Typography>
                  <Typography variant="h4" color="primary.main">
                    {formatCurrency(overheadResults.overheadHourly)}
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" color="text.secondary">Per Minute</Typography>
                  <Typography variant="h5" color="primary.main">
                    {formatCurrency(overheadResults.overheadPerMinute)}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">Total Annual Expenses</Typography>
                  <Typography variant="body1">{formatCurrency(overheadResults.annualOverhead)}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">Calculation Period</Typography>
                  <Typography variant="body2">{overheadResults.period}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">Utilization Rate</Typography>
                  <Typography variant="body2">{overheadResults.utilizationRate}%</Typography>
                </Box>

                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    These rates are automatically used in service pricing calculations
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          ) : (
            <Card elevation={2}>
              <CardContent>
                <Box textAlign="center" py={4}>
                  <Calculate sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Calculate Overhead Rates
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Click the button above to calculate your per-minute overhead rate based on actual expenses
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}

          <Card elevation={2} sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Quick Stats</Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">Total Expense Records</Typography>
                <Typography variant="h6">{expenses.length}</Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">Categories</Typography>
                <Typography variant="h6">{allCategories.length}</Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">Included in Overhead</Typography>
                <Typography variant="h6">{includedCategories.length}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={configDialogOpen} onClose={() => setConfigDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Overhead Calculation Settings</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            <Typography variant="body2">
              Stock, Payroll, Taxation, and Personal are always excluded and cannot be changed.
              Any other categories can be excluded here if they should not contribute to overhead.
            </Typography>
          </Alert>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Calculation Period</InputLabel>
            <Select
              value={config.calculation_period}
              label="Calculation Period"
              onChange={(e) => setConfig({ ...config, calculation_period: e.target.value })}
            >
              <MenuItem value="current">Current Month</MenuItem>
              <MenuItem value="last3months">Last 3 Months</MenuItem>
              <MenuItem value="last6months">Last 6 Months</MenuItem>
              <MenuItem value="last12months">Last 12 Months</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            type="number"
            label="Staff Utilization Rate (%)"
            value={config.utilization_rate}
            onChange={(e) => setConfig({ ...config, utilization_rate: parseFloat(e.target.value) })}
            helperText="Percentage of time spent on paid services (typically 70–90%)"
            sx={{ mb: 2 }}
            inputProps={{ min: 1, max: 100 }}
          />

          <TextField
            fullWidth
            type="number"
            label="Weekly Trading Hours"
            value={config.total_trading_hours}
            onChange={(e) => setConfig({ ...config, total_trading_hours: parseFloat(e.target.value) })}
            helperText="Total hours salon is open per week"
            inputProps={{ min: 1, max: 168 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveConfig} variant="contained">Save Configuration</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OverheadsCalculator;